import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Card, CardBody, Heading, Text, HStack, VStack, Wrap, WrapItem,
  Tag, TagLabel, TagLeftIcon, Select, Button, Badge, Spinner, useToast, Flex, Icon, Divider,
} from '@chakra-ui/react';
import { FaFileInvoiceDollar, FaFlask, FaCheck, FaPlusCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';

// "Completed by the lab" = dropped-off-and-later. dropped_off_lab / shipped move
// the submission to waiting_on_lab_results; completed = results posted. We only
// surface orders not yet on an invoice (invoice_id is null).
const OPEN_STATUSES = ['waiting_on_lab_results', 'completed'];

// Mirror BatchManagement's numbering schemes so invoices created here match.
async function generateBatchNumber() {
  const yearStr = new Date().getFullYear().toString();
  let seqNum = 1;
  let batchNumber;
  let attempts = 0;
  while (attempts < 999) {
    batchNumber = `BATCH-${yearStr}-${seqNum.toString().padStart(3, '0')}`;
    const { data, error } = await supabase
      .from('submission_batches').select('id').eq('batch_number', batchNumber).maybeSingle();
    if (error) break;
    if (data === null) return batchNumber;
    seqNum++; attempts++;
  }
  return `BATCH-${yearStr}-${Date.now().toString().slice(-3)}`;
}

async function generateInvoiceNumber() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // excludes 0/O/I/1
  let attempts = 0;
  while (attempts < 100) {
    let n = '';
    for (let i = 0; i < 4; i++) n += chars[Math.floor(Math.random() * chars.length)];
    const { data, error } = await supabase
      .from('submission_invoices').select('id').eq('invoice_number', n).maybeSingle();
    if (error) break;
    if (data === null) return n;
    attempts++;
  }
  return Date.now().toString().slice(-4).toUpperCase();
}

/**
 * Pick completed (dropped-off-and-later) orders for a single lab and bundle them
 * into a new invoice. Invoices are per-lab (charged to the lab's Stripe customer),
 * so the lab filter scopes which orders you can select.
 *
 * On create: makes a batch, assigns the selected submissions to it, generates the
 * submission_invoices row, and links invoice_id back onto the submissions — the
 * same shape BatchManagement produces, so the invoice shows up under Manage Invoices.
 */
export default function OpenOrdersInvoiceBuilder({ onInvoiceCreated }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labId, setLabId] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await selectAll(() => supabase
        .from('submissions')
        .select('id, patient_name, lab_id, status, submitted_at, labs ( id, name, stripe_customer_id )')
        .in('status', OPEN_STATUSES)
        .is('invoice_id', null)
        .not('lab_id', 'is', null) // invoices are per-lab; skip unassigned orders
        .order('submitted_at', { ascending: false }));
      if (error) throw error;
      setOrders(data || []);
    } catch (e) {
      toast({ title: 'Could not load open orders', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Labs that actually have open orders, with counts, for the filter dropdown.
  const labsWithCounts = useMemo(() => {
    const m = new Map();
    for (const o of orders) {
      const key = o.lab_id == null ? 'none' : String(o.lab_id);
      if (!m.has(key)) {
        m.set(key, { id: o.lab_id || null, name: o.labs?.name || 'Unassigned lab', stripe: o.labs?.stripe_customer_id || null, count: 0 });
      }
      m.get(key).count++;
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [orders]);

  const selectedLab = useMemo(
    () => labsWithCounts.find((l) => String(l.id) === String(labId)) || null,
    [labsWithCounts, labId]
  );

  // Orders shown as chips — only once a lab is chosen (one invoice per lab).
  const visible = useMemo(
    () => (labId ? orders.filter((o) => String(o.lab_id) === String(labId)) : []),
    [orders, labId]
  );

  const onLabChange = (e) => { setLabId(e.target.value); setSelected(new Set()); };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(visible.map((o) => o.id)));
  const clearSel = () => setSelected(new Set());

  const createInvoice = async () => {
    const ids = visible.filter((o) => selected.has(o.id)).map((o) => o.id);
    if (!ids.length) return;
    setCreating(true);
    try {
      // 1) batch — batch_name is NOT NULL, so always provide one.
      const batchNumber = await generateBatchNumber();
      const batchName = `${(selectedLab?.name || 'Invoice').toUpperCase()} (${new Date().toLocaleDateString()})`;
      const { data: batch, error: bErr } = await supabase
        .from('submission_batches')
        .insert({ batch_number: batchNumber, batch_name: batchName, status: 'pending' })
        .select().single();
      if (bErr) throw bErr;

      // 2) assign the selected submissions to the batch
      const { error: aErr } = await supabase.from('submissions').update({ batch_id: batch.id }).in('id', ids);
      if (aErr) throw aErr;

      // 3) generate the invoice record
      const invoiceNumber = await generateInvoiceNumber();
      const { data: invoice, error: iErr } = await supabase
        .from('submission_invoices')
        .insert({
          invoice_number: invoiceNumber,
          original_batch_id: batch.id,
          status: 'pending',
          ship_fee: 0,
          shipping_method: 'fedex',
          invoice_generated_at: new Date().toISOString(),
        })
        .select().single();
      if (iErr) throw iErr;

      // 4) link the invoice back onto the submissions
      const { error: lErr } = await supabase.from('submissions').update({ invoice_id: invoice.id }).in('id', ids);
      if (lErr) throw lErr;

      toast({
        title: `Invoice ${invoiceNumber} created`,
        description: `${ids.length} order${ids.length === 1 ? '' : 's'} from ${selectedLab?.name || 'lab'}. Opening preview…`,
        status: 'success', duration: 3000, isClosable: true,
      });

      // Drop the invoiced orders from the open list + reset selection.
      setOrders((prev) => prev.filter((o) => !ids.includes(o.id)));
      setSelected(new Set());
      if (onInvoiceCreated) onInvoiceCreated(invoice);

      // Take the admin straight to the invoice preview — the public invoice
      // page when it has a share token, otherwise the admin invoices list
      // focused on this batch.
      if (invoice.public_token) {
        navigate(`/invoice/${invoice.public_token}`);
      } else {
        navigate(`/admin/invoices?batch=${batch.id}`);
      }
    } catch (e) {
      toast({ title: 'Could not create invoice', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setCreating(false);
    }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return ''; } };
  const selCount = visible.filter((o) => selected.has(o.id)).length;

  return (
    <Card mb={4} borderWidth="1px" borderColor="purple.100" shadow="sm">
      <CardBody>
        <HStack mb={1} spacing={2}>
          <Icon as={FaFileInvoiceDollar} color="purple.500" />
          <Heading size="sm">Create invoice from completed orders</Heading>
        </HStack>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Dropped-off &amp; later orders not yet on an invoice. Pick a lab, select the orders, and create its invoice.
        </Text>

        <Flex gap={3} align="center" wrap="wrap" mb={3}>
          <HStack spacing={2}>
            <Icon as={FaFlask} color="gray.400" boxSize={3} />
            <Select
              size="sm" maxW="320px" placeholder={loading ? 'Loading…' : 'Select a lab…'}
              value={labId} onChange={onLabChange} isDisabled={loading || labsWithCounts.length === 0}
            >
              {labsWithCounts.map((l) => (
                <option key={l.id ?? 'none'} value={l.id ?? ''}>
                  {l.name} ({l.count})
                </option>
              ))}
            </Select>
          </HStack>

          {labId && visible.length > 0 && (
            <HStack spacing={2}>
              <Button size="xs" variant="outline" onClick={selectAll}>Select all ({visible.length})</Button>
              <Button size="xs" variant="ghost" onClick={clearSel} isDisabled={selCount === 0}>Clear</Button>
            </HStack>
          )}
        </Flex>

        {loading ? (
          <HStack color="gray.500" py={6} justify="center"><Spinner size="sm" /><Text fontSize="sm">Loading open orders…</Text></HStack>
        ) : labsWithCounts.length === 0 ? (
          <Text fontSize="sm" color="gray.500" py={4}>No completed orders are waiting to be invoiced. 🎉</Text>
        ) : !labId ? (
          <Text fontSize="sm" color="gray.500" py={4}>Select a lab above to see its open orders.</Text>
        ) : visible.length === 0 ? (
          <Text fontSize="sm" color="gray.500" py={4}>No open orders for this lab.</Text>
        ) : (
          <>
            {selectedLab && !selectedLab.stripe && (
              <Text fontSize="xs" color="gray.500" mb={2}>
                {selectedLab.name} has no saved card on file — collect this invoice via ACH or a payment link. (Card charging would need a Stripe profile, but that's not required for ACH.)
              </Text>
            )}
            <Wrap spacing={2}>
              {visible.map((o) => {
                const isSel = selected.has(o.id);
                return (
                  <WrapItem key={o.id}>
                    <Tag
                      size="lg" borderRadius="full" cursor="pointer" userSelect="none"
                      variant={isSel ? 'solid' : 'subtle'}
                      colorScheme={isSel ? 'purple' : 'gray'}
                      onClick={() => toggle(o.id)}
                    >
                      {isSel && <TagLeftIcon as={FaCheck} boxSize={3} />}
                      <TagLabel>
                        {o.patient_name || `#${o.id}`}{' '}
                        <Text as="span" fontSize="xs" opacity={0.7}>#{o.id} · {fmtDate(o.submitted_at)}</Text>
                      </TagLabel>
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>

            <Divider my={4} />
            <HStack justify="space-between" wrap="wrap" spacing={3}>
              <Badge colorScheme={selCount ? 'purple' : 'gray'} fontSize="sm" px={2} py={1} borderRadius="md">
                {selCount} selected
              </Badge>
              <Button
                colorScheme="purple" size="sm" leftIcon={<FaPlusCircle />}
                isDisabled={selCount === 0} isLoading={creating} loadingText="Creating invoice…"
                onClick={createInvoice}
              >
                Create invoice from {selCount} order{selCount === 1 ? '' : 's'}
              </Button>
            </HStack>
          </>
        )}
      </CardBody>
    </Card>
  );
}
